/**
 * The main front-end entry point for the calendar app.
 */

import { render } from 'preact';
import * as cal from './cal/routes';
import * as courses from './courses/routes';
import { Toaster } from '@components/toaster';
import { createHistoryRouter, AppRouter, RouteRule, RouterPage } from '@components/router';
import { GlobalComponents } from '@components/dialog';
import { AuthContextProvider } from './lib/auth';
import { CoursesApp } from './courses';
import { CalendarLandingPageApp } from './components/cal-landing-page-app';

import { FeedbackWidget } from '@components/feedback/feedback-widget';
import { RuzukuGlobal } from 'server/types';

declare global {
  interface Window {
    ruzukuGlobal?: RuzukuGlobal;
  }
}

/**
 * Render the root component, and wire up the application context.
 */
function App(props: { router: AppRouter }) {
  return (
    <AuthContextProvider>
      <RouterPage router={props.router}>
        <GlobalComponents />
        <CoursesApp />
      </RouterPage>
      <Toaster />
      <FeedbackWidget />
    </AuthContextProvider>
  );
}

function addRoutes(router: AppRouter, app: { routes: RouteRule[] }) {
  app.routes.forEach(([loadModule, ...patterns]) =>
    patterns.forEach((url) => router.add({ loadModule, url })),
  );
}

/**
 * Mount the application.
 */
function mountApp() {
  const mainElement = document.querySelector('main');
  if (!mainElement) {
    console.error('Main element not found - cannot mount application');
    return;
  }

  // Check if we should render the calendar landing page instead
  if (window.ruzukuGlobal?.app === 'cal-landing-page') {
    render(<CalendarLandingPageApp />, mainElement);
    return;
  }

  // Render the main app
  const router: AppRouter = createHistoryRouter();
  addRoutes(router, cal);
  addRoutes(router, courses);
  render(<App router={router} />, mainElement);
}

mountApp();
